import { translate } from "app/filters/i18nFilter";
import type { SignalNameSegments } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalNameSegments";
import type SignalNameWithCountry from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalNameWithCountry";
import type EcommerceSignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalFilters";
import EcommerceSignalSubType from "app/pages/sales-intelligence/sub-modules/signals/models/ecommerce/EcommerceSignalSubType";
import { coerceEcommerceFiltersForForm } from "app/pages/sales-intelligence/sub-modules/signals/utils/ecommerce/ecommerceFilterNormalization";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";
import type Signal from "../../../models/core/Signal";
import type UISignalNameBuilder from "../UISignalNameBuilder";
import { boldHtml } from "app/pages/sales-intelligence/sub-modules/signals/utils/boldSignalValue";

const NOT_ENOUGH_DATA = "Not enough data to build ecommerce signal name";

function buildChangeSignalName(nameKey: string, direction: string, period: string): Readonly<SignalNameWithCountry> {
    return {
        key: nameKey,
        replacements: {
            direction: boldHtml(translate(signalsI18nKeys.ecommerce.directionLabel(direction))),
            period: translate(signalsI18nKeys.ecommerce.periodLabel(period)),
        },
    };
}

function buildReachedSignalName(nameKey: string, bucket: string): Readonly<SignalNameWithCountry> {
    return {
        key: nameKey,
        replacements: { bucket: boldHtml(bucket) },
    };
}

function buildChangeSegments(direction: string, period: string, titleKey: string, descKey: string): SignalNameSegments {
    return {
        signalType: {
            key: titleKey,
            replacements: {
                direction: translate(signalsI18nKeys.ecommerce.directionLabel(direction)),
            },
        },
        signalData: {
            key: descKey,
            replacements: {
                period: { key: signalsI18nKeys.ecommerce.periodLabel(period) },
            },
        },
    };
}

function buildReachedSegments(bucket: string, titleKey: string, descKey: string): SignalNameSegments {
    return {
        signalType: {
            key: titleKey,
            replacements: {},
        },
        signalData: {
            key: descKey,
            replacements: { bucket },
        },
    };
}

export default function createEcommerceUISignalNameBuilder(): UISignalNameBuilder<EcommerceSignalFilters> {
    function buildName(signal: Signal<EcommerceSignalFilters>): Readonly<SignalNameWithCountry> {
        const normalized = coerceEcommerceFiltersForForm(signal.signalSubType, signal.filters);

        switch (signal.signalSubType) {
            case EcommerceSignalSubType.OnlineRevenueChange:
            case EcommerceSignalSubType.OnlineTransactionsChange: {
                const change = normalized.change;

                if (!change?.direction || !change.period) {
                    throw new Error(NOT_ENOUGH_DATA);
                }

                const isRevenue = signal.signalSubType === EcommerceSignalSubType.OnlineRevenueChange;
                return buildChangeSignalName(
                    isRevenue
                        ? signalsI18nKeys.ecommerce.signalName.onlineRevenueChange
                        : signalsI18nKeys.ecommerce.signalName.onlineTransactionsChange,
                    change.direction,
                    change.period,
                );
            }
            case EcommerceSignalSubType.OnlineRevenueReached:
            case EcommerceSignalSubType.OnlineTransactionsReached: {
                const bucket = normalized.bucketTransition?.bucket;

                if (typeof bucket !== "string") {
                    throw new Error(NOT_ENOUGH_DATA);
                }

                const isRevenue = signal.signalSubType === EcommerceSignalSubType.OnlineRevenueReached;
                return buildReachedSignalName(
                    isRevenue
                        ? signalsI18nKeys.ecommerce.signalName.onlineRevenueReached
                        : signalsI18nKeys.ecommerce.signalName.onlineTransactionsReached,
                    bucket,
                );
            }
            default:
                throw new Error(NOT_ENOUGH_DATA);
        }
    }

    function buildNameSegments(signal: Signal<EcommerceSignalFilters>): SignalNameSegments {
        const normalized = coerceEcommerceFiltersForForm(signal.signalSubType, signal.filters);

        switch (signal.signalSubType) {
            case EcommerceSignalSubType.OnlineRevenueChange:
            case EcommerceSignalSubType.OnlineTransactionsChange: {
                const change = normalized.change;

                if (!change?.direction || !change.period) {
                    throw new Error(NOT_ENOUGH_DATA);
                }

                const isRevenue = signal.signalSubType === EcommerceSignalSubType.OnlineRevenueChange;
                return buildChangeSegments(
                    change.direction,
                    change.period,
                    isRevenue
                        ? signalsI18nKeys.ecommerce.segments.onlineRevenueChangeTitle
                        : signalsI18nKeys.ecommerce.segments.onlineTransactionsChangeTitle,
                    isRevenue
                        ? signalsI18nKeys.ecommerce.segments.onlineRevenueChangeDescription
                        : signalsI18nKeys.ecommerce.segments.onlineTransactionsChangeDescription,
                );
            }
            case EcommerceSignalSubType.OnlineRevenueReached:
            case EcommerceSignalSubType.OnlineTransactionsReached: {
                const bucket = normalized.bucketTransition?.bucket;

                if (typeof bucket !== "string") {
                    throw new Error(NOT_ENOUGH_DATA);
                }

                const isRevenue = signal.signalSubType === EcommerceSignalSubType.OnlineRevenueReached;
                return buildReachedSegments(
                    bucket,
                    isRevenue
                        ? signalsI18nKeys.ecommerce.segments.onlineRevenueReachedTitle
                        : signalsI18nKeys.ecommerce.segments.onlineTransactionsReachedTitle,
                    isRevenue
                        ? signalsI18nKeys.ecommerce.segments.onlineRevenueReachedDescription
                        : signalsI18nKeys.ecommerce.segments.onlineTransactionsReachedDescription,
                );
            }
            default:
                throw new Error(NOT_ENOUGH_DATA);
        }
    }

    return { buildName, buildNameSegments, buildSelectionTooltipLabels: () => undefined };
}
